import { t } from "@/lib/i18n/interpolate";
import {
  computeLogarithm,
  type LogError,
  type LogResult,
  logName,
  type Operand,
  parseOperand,
} from "@/lib/tool-kit/arithmetic/logarithm";
import {
  computePower,
  type PowerView,
} from "@/lib/tool-kit/arithmetic/power-view";
import { formatFraction, toNumberR } from "@/lib/tool-kit/math/rational";

/**
 * The logarithm calculator's three modes: log_b x, the antilogarithm b^y, and solving
 * b^x = y (the same number as log_b y, with the working of taking logs of both sides).
 */
export type LogMode = "log" | "antilog" | "solve";

export interface LogView {
  mode: "log" | "solve";
  base: Operand;
  x: Operand;
  result: LogResult;
  name: string;
}

export interface AntilogView {
  base: Operand;
  y: Operand;
  /** For base e: the value as a double. */
  exp: number | null;
  /** For other bases: the exact or irrational power. */
  power: PowerView | null;
  name: string;
}

export type LogComputation =
  | { kind: "empty" | "invalid-base" | "invalid-x" | LogError }
  | { kind: "log"; view: LogView }
  | { kind: "antilog"; view: AntilogView }
  | { kind: "antilog-failed" };

export function computeLogMode(
  mode: LogMode,
  rawBase: string,
  rawX: string,
  places: number
): LogComputation {
  if (rawBase.trim() === "" || rawX.trim() === "") {
    return { kind: "empty" };
  }
  const base = parseOperand(rawBase);
  if (base === null) {
    return { kind: "invalid-base" };
  }
  const x = parseOperand(rawX);
  if (x === null) {
    return { kind: "invalid-x" };
  }
  if (mode === "antilog") {
    return antilog(base, x, rawBase, rawX, places);
  }
  const result = computeLogarithm(base, x);
  if ("error" in result) {
    return { kind: result.error };
  }
  return {
    kind: "log",
    view: { mode, base, x, result, name: logName(base) },
  };
}

function antilog(
  base: Operand,
  y: Operand,
  rawBase: string,
  rawY: string,
  places: number
): LogComputation {
  const name = logName(base);
  if (base.rational && base.rational.num <= BigInt(0)) {
    return { kind: "base-invalid" };
  }
  if (y.isE || y.rational === null) {
    return { kind: "invalid-x" };
  }
  if (base.isE) {
    const exp = Math.exp(toNumberR(y.rational));
    return { kind: "antilog", view: { base, y, exp, power: null, name } };
  }
  const outcome = computePower(rawBase, rawY, "2", places);
  if (outcome.kind !== "ok") {
    return { kind: "antilog-failed" };
  }
  return {
    kind: "antilog",
    view: { base, y, exp: null, power: outcome.view, name },
  };
}

/* -------------------------------------------------------------- narration */

export interface LogWords {
  definition: string;
  exactPower: string;
  zeroRule: string;
  oneRule: string;
  changeOfBase: string;
  solveStart: string;
  solveTake: string;
  solveIsolate: string;
  check: string;
}

function exactLine(view: LogView, words: LogWords): string {
  const { base, x, result, name } = view;
  const exact = result.exact;
  if (exact === null) {
    return "";
  }
  if (exact.num === BigInt(0)) {
    return t(words.zeroRule, { name });
  }
  if (exact.num === BigInt(1) && exact.den === BigInt(1)) {
    return t(words.oneRule, { name, b: base.text });
  }
  const k = formatFraction(exact);
  const plain = exact.den === BigInt(1) && exact.num > BigInt(0);
  return t(words.exactPower, {
    x: x.text,
    b: base.text,
    k,
    kp: plain ? k : `(${k})`,
    name,
  });
}

export function logLines(
  view: LogView,
  words: LogWords,
  fmt: (value: number) => string
): string[] {
  const { base, x, result, name } = view;
  const b = base.text;
  const lines: string[] = [];
  if (view.mode === "solve") {
    lines.push(
      t(words.solveStart, { b, y: x.text }),
      t(words.solveTake, { b, y: x.text }),
      t(words.solveIsolate, {
        y: x.text,
        b,
        lnY: fmt(result.lnX),
        lnB: fmt(result.lnBase),
        value: fmt(result.value),
      })
    );
  } else {
    lines.push(t(words.definition, { name, b, x: x.text }));
  }
  if (result.exact) {
    lines.push(exactLine(view, words));
    return lines;
  }
  if (view.mode === "log") {
    lines.push(
      t(words.changeOfBase, {
        name,
        x: x.text,
        lnX: fmt(result.lnX),
        lnB: fmt(result.lnBase),
        value: fmt(result.value),
        b,
      })
    );
  }
  if (result.roundTrip !== null) {
    lines.push(
      t(words.check, {
        b,
        value: fmt(result.value),
        back: fmt(result.roundTrip),
      })
    );
  }
  return lines;
}
